import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import QRCode from "react-qr-code";
import styled from "styled-components";
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faPrint, faInfoCircle, faCreditCard, faTimesCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import BadgeEstadoTurno from '../../turno/components/BadgeEstadoTurno';
import TituloFiltro from '../../turno/components/TituloFiltro';
import { enumEstadoTurno } from '../../../utils/constants/estadoTurno.constants';
import { formatDate } from '../../../utils/helpers/date.helpers';
import { useAuthContext } from "../../../context/auth";
import Contenedor from '../../../components/generalStructure/Contenedor/Contenedor';
import { ButtonConfirm, ButtonVolver } from '../../../components/Inputs/Buttons';
import BotonSuccess from '../../../components/Inputs/Buttons/ButtonConfirm/Botones/BotonSuccess';
import { useParams } from 'react-router-dom'
import { useTurnos } from '../../../services/useTurnos.service'
import ListaInvitados from '../../invitados/components/ListaInvitados';
import MercadoPagoBrick from '../../../components/MercadoPagoBrick';
import { crearPreferenciaPago, consultarEstadoPago } from '../../../services/pagoService';


const AlertaTurno = styled.div`
    background-color: ${({ theme }) => theme.backgroundAlerta};
    color: ${({ theme }) => theme.textAlerta};
`;
const InfoBox = styled.div`
    background-color: ${({ theme }) => theme.body};
    border-radius: 0.25rem;
    padding: 1rem;
    border: 1px solid ${({ theme }) => theme.text}20; /* Borde con opacidad */
`;

const TextMuted = styled.span`
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
`;

const ContenedorTurno = () => {

    const [ service, error, loading ] = useTurnos()
    const [ turno, setTurno ] = useState(null)
    const { afiliado } = useAuthContext()
    const { id } = useParams()

    // Estados para el flujo de pago
    const [preferenceId, setPreferenceId] = useState(null);
    const [isLoadingPayment, setIsLoadingPayment] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [pagoData, setPagoData] = useState(null);
    const [isLoadingPagoData, setIsLoadingPagoData] = useState(false);
    const [pagoStatus, setPagoStatus] = useState('pending'); // 'pending', 'created', 'approved', 'expired', 'not_found'

    const [idPago, setIdPago] = useState(null);

    // Ref para verificar si el componente está montado
    const isMountedRef = useRef(true);
    const hasLoadedTurnoRef = useRef(false);
    const turnoLoadedSuccessfullyRef = useRef(false);

    // ============================================
    // HELPER FUNCTIONS - Fechas
    // ============================================
    // Helper para normalizar fecha a medianoche
    const normalizarFecha = (fecha) => {
        const fechaNormalizada = new Date(fecha);
        fechaNormalizada.setHours(0, 0, 0, 0);
        return fechaNormalizada;
    };

    // Helper para verificar si una fecha es pasada
    const esFechaPasada = (fechaTurno) => {
        if (!fechaTurno) return false;
        const fechaTurnoNormalizada = normalizarFecha(fechaTurno);
        const fechaHoy = normalizarFecha(new Date());
        return fechaTurnoNormalizada < fechaHoy;
    };

    const tarifaNumerica = useMemo(() => {
        if (!turno) return null;
        const valor = Number(turno.tarifa);
        return Number.isNaN(valor) ? null : valor;
    }, [turno]);

    const esPagoBonificado = useMemo(() => tarifaNumerica === 0, [tarifaNumerica]);

    const montoDisplay = useMemo(() => {
        if (!turno) return '';
        if (esPagoBonificado) return 'Pago Bonificado';
        if (tarifaNumerica !== null && tarifaNumerica >= 0) {
            return `$${tarifaNumerica.toLocaleString('es-AR')}`;
        }
        if (typeof turno.tarifa === 'string' && turno.tarifa.trim().length > 0) {
            return `$${turno.tarifa}`;
        }
        return '$0';
    }, [turno, esPagoBonificado, tarifaNumerica]);

    // Determinar el estado de la fecha del turno (hoy, futuro, pasado)
    const estadoFechaTurno = useMemo(() => {
        if (!turno || !turno.fechaCalendario?.fecha) return null;
        const fechaTurno = normalizarFecha(turno.fechaCalendario.fecha);
        const fechaHoy = normalizarFecha(new Date());
        
        if (fechaTurno < fechaHoy) {
            return 'pasado'; // Fecha del turno es menor a hoy
        } else if (fechaTurno > fechaHoy) {
            return 'futuro'; // Fecha del turno es mayor a hoy
        } else {
            return 'hoy'; // Fecha del turno es hoy
        }
    }, [turno]);

    // ============================================
    // FUNCIONES DE GESTIÓN DE ESTADOS
    // ============================================
    // Función para establecer el estado de pago de forma centralizada
    const establecerEstadoPago = useCallback((nuevoEstado, preferenceIdValue = null, idPagoValue = null, pagoDataValue = null) => {
        if (!isMountedRef.current) return;
        
        setPagoStatus(nuevoEstado);
        setPreferenceId(preferenceIdValue);
        setIdPago(idPagoValue);
        if (pagoDataValue !== null) {
            setPagoData(pagoDataValue);
        }
    }, []);

    // Función para procesar el estado del pago según las reglas de negocio
    const procesarEstadoPago = useCallback((pago, fechaTurno) => {
        if (!isMountedRef.current) return;

        // PRIORIDAD 1: Si está approved, no importa la fecha
        if (pago.estado === 'approved') {
            establecerEstadoPago('approved', null, null);
            return;
        }

        // PRIORIDAD 2: Si NO está approved y la fecha es pasada, es expired
        if (esFechaPasada(fechaTurno)) {
            establecerEstadoPago('expired', null, null);
            return;
        }

        // PRIORIDAD 3: Procesar otros estados según expiración
        const hoy = new Date();
        const fechaExpiracion = new Date(pago.fechaExpiracion);

        if (pago.estado === 'created') {
            if (hoy <= fechaExpiracion) {
                // Pago creado y no expirado
                establecerEstadoPago('created', pago.preferenceId, pago.id);
            } else {
                // Pago creado pero expirado
                establecerEstadoPago('expired', null, null);
            }
        } else {
            // Otros estados (pending, rejected, etc.)
            establecerEstadoPago('created', pago.preferenceId, pago.id);
        }
    }, [establecerEstadoPago]);

    // ============================================
    // FUNCIONES DE SERVICIO
    // ============================================
    /**
     * Consulta el estado de pago del turno
     */
    const consultarPagoTurno = useCallback(async (turnoId, fechaTurno) => {
        try {
            if (!isMountedRef.current) return; // Si está desmontado, no hacer nada
            
            setIsLoadingPagoData(true);
            setPaymentError(null);

            console.log('Consultando estado de pago para turno:', turnoId);
            const respuesta = await consultarEstadoPago(turnoId);
            
            if (!isMountedRef.current) return; // Verificar después de la petición

            if (!respuesta) {
                // No existe pago para este turno
                console.log('No existe pago para este turno');
                
                const nuevoEstado = esFechaPasada(fechaTurno) ? 'expired' : 'not_found';
                
                establecerEstadoPago(nuevoEstado, null, null, null);
                return;
            }

            // Existe pago, procesar estado
            const pago = respuesta.pago;
            setPagoData(pago);

            const hoy = new Date();
            const fechaExpiracion = new Date(pago.fechaExpiracion);

            console.log('Estado del pago:', pago.estado);
            console.log('Fecha de expiración:', fechaExpiracion);
            console.log('Fecha actual:', hoy);

            procesarEstadoPago(pago, fechaTurno);

        } catch (error) {
            if (!isMountedRef.current) return; // Si está desmontado, no actualizar estado
            
            console.error('Error al consultar estado de pago:', error);
            setPaymentError(error.message || 'Error al consultar estado de pago');
            establecerEstadoPago('not_found', null, null);
        } finally {
            if (isMountedRef.current) {
                setIsLoadingPagoData(false);
            }
        }
    }, [establecerEstadoPago, procesarEstadoPago]);

    // ============================================
    // EFFECTS
    // ============================================
    // Cleanup: marcar como desmontado cuando el componente se desmonte
    useEffect(() => {
        isMountedRef.current = true;
        hasLoadedTurnoRef.current = false;
        turnoLoadedSuccessfullyRef.current = false;
        return () => {
            isMountedRef.current = false;
            hasLoadedTurnoRef.current = false;
            turnoLoadedSuccessfullyRef.current = false;
        };
    }, []);

    useEffect(() => {
        if(!id) return;
        if(hasLoadedTurnoRef.current) return; // Evitar múltiples cargas
        
        hasLoadedTurnoRef.current = true;
        
        console.log('Consultando turno:', id);

        service.getById(
            id,
            async (turnoObtenido) => { 
                try {
                    // Marcar que el turno se cargó exitosamente
                    turnoLoadedSuccessfullyRef.current = true;
                    setTurno(turnoObtenido);
                    console.log('Turno obtenido:', turnoObtenido);

                    const tarifa = Number(turnoObtenido.tarifa);

                    console.log('Tarifa:', tarifa);
                    
                    if (!Number.isNaN(tarifa) && tarifa === 0) {
                        // Pago bonificado
                        establecerEstadoPago('approved', null, null, {
                            estado: 'approved',
                            monto: 0,
                            descripcion: 'Pago bonificado'
                        });

                        console.log('Estado de pago establecido:', 'approved');
                    } else {
                        console.log('Estamos en el else');
                        // Verificar la fecha del turno antes de consultar el pago
                        const fechaTurno = turnoObtenido.fechaCalendario?.fecha;
                        
                        if (!fechaTurno) {
                            console.warn('No se encontró fecha del turno');
                            establecerEstadoPago('not_found', null, null);
                            return;
                        }
                        
                        const fechaTurnoNormalizada = normalizarFecha(fechaTurno);
                        const fechaHoy = normalizarFecha(new Date());

                        console.log('Fecha turno normalizada:', fechaTurnoNormalizada);

                        if (fechaTurnoNormalizada > fechaHoy) {
                            // Fecha del turno es mayor a hoy - no consultar pago todavía
                            establecerEstadoPago('not_found', null, null);
                        } else {
                            // Fecha del turno es hoy o pasada - SIEMPRE consultar estado de pago primero
                            // Manejar errores de consultarPagoTurno sin afectar la visualización del turno
                            try {
                                await consultarPagoTurno(id, fechaTurno);
                            } catch (error) {
                                console.error('Error al consultar pago (no crítico):', error);
                                // No mostrar toast aquí, solo establecer estado por defecto
                                establecerEstadoPago('not_found', null, null);
                            }
                        }

                        console.log('Estamos en el final del if');
                    }
                } catch (error) {
                    console.error('Error en el callback de éxito de getById:', error);
                    // No mostrar toast aquí porque el turno se cargó correctamente
                    // Solo loguear el error para debugging
                }
            },
            (error) => {
                // Verificar si el componente está montado antes de procesar el error
                if (!isMountedRef.current) {
                    console.log('Componente desmontado, ignorando error');
                    return;
                }

                // Verificar si el error es de tipo "abort" (petición cancelada)
                const isAbortError = error?.name === 'AbortError' || 
                                   error?.message?.toLowerCase().includes('aborted') ||
                                   error?.message?.toLowerCase().includes('signal is aborted');
                
                if (isAbortError) {
                    console.log('Petición abortada, ignorando error:', error);
                    hasLoadedTurnoRef.current = false; // Permitir reintentar
                    return;
                }

                // Si el turno ya se cargó correctamente, no mostrar error
                // (puede ser un error de una petición secundaria o abortada después del éxito)
                if (turnoLoadedSuccessfullyRef.current) {
                    console.log('Turno ya cargado exitosamente, ignorando error posterior:', error);
                    hasLoadedTurnoRef.current = false;
                    return;
                }

                // Solo mostrar error si es un error real, el componente está montado y el turno no se cargó
                hasLoadedTurnoRef.current = false; // Permitir reintentar en caso de error
                console.error('Error al buscar el turno:', error);
                toast.error(`Hubo un error al buscar el turno: ${error.message || 'Error desconocido'}`);
            }
        )
    }, [id, establecerEstadoPago, consultarPagoTurno]); //eslint-disable-line

    const anularTurno = () => {
        service.del(
            {id, dni: afiliado.dni},
            () => { 
                setTurno(prevState => ({
                    ...prevState, 
                    ["estadoTurnoID"]: enumEstadoTurno.ANULADO.id
                }));
                toast.success("El turno fue anulado con éxito");
            },
            () => toast.error("Hubo un error al intentar anular el turno")
        )
    };

    /**
     * Maneja la generación de la preferencia de pago
     * Se ejecuta cuando el usuario quiere pagar el turno
     */
    const handleGenerarPago = useCallback(async () => {
        try {
            setIsLoadingPayment(true);
            setPaymentError(null);

            // Solo generar pago si no existe uno previo
            if (pagoStatus !== 'not_found') {
                console.log('Ya existe un pago para este turno');
                toast.info("Ya existe un pago para este turno");
                setIsLoadingPayment(false);
                return;
            }

            if (Number(turno?.tarifa) === 0 || esPagoBonificado) {
                toast.info("Este turno está bonificado y no requiere pago.");
                setPagoStatus('approved');
                setIsLoadingPayment(false);
                return;
            }

            // Construir descripción corta del turno (solo lugar, turno y DNI titular)
            const descripcion = `${turno.fechaCalendario.centroRecreativo.descripcion} - Turno ${turno.id} - DNI ${afiliado.dni}`;
           
            // Calcular fecha de caducidad (18hs del día del turno)
            const fechaTurno = new Date(turno.fechaCalendario.fecha);
            const fechaCaducidad = new Date(fechaTurno);
            fechaCaducidad.setHours(18, 0, 0, 0); // 18:00:00 del día del turno 

            // Construir objeto con datos del pago
            const datosPago = {
                "titulo": "Turno Cris",
                "descripcion": descripcion,
                "precio": tarifaNumerica ?? 0,
                "emailComprador": afiliado.email || '',
                "urlExito": window.__CONFIG__.urlVolver + parseInt(turno.id),
                "urlPendiente": window.__CONFIG__.urlVolver + parseInt(turno.id),
                "urlFallo": window.__CONFIG__.urlVolver + parseInt(turno.id),
                "referencia": parseInt(turno.id),
                "origen": 2,
                "fechaInicioLink": fechaTurno.toISOString(),
                "fechaCaducidadLink": fechaCaducidad.toISOString()
            };

            console.log('Generando preferencia de pago con datos:', datosPago);

            // Llamar al servicio para crear la preferencia
            const respuesta = await crearPreferenciaPago(datosPago);
            console.log('Respuesta desde contenedorTurno:', respuesta);
            

            // Extraer el ID de la preferencia de la respuesta
            if (respuesta && respuesta.preference) {
                const preferenceId = respuesta.preference;
                setPreferenceId(preferenceId);
                setIdPago(respuesta.id);
                setPagoStatus('created');
                toast.success("Preferencia de pago generada correctamente");
                console.log('Preferencia ID obtenida:', preferenceId);
            } else {
                throw new Error('Respuesta inválida del servidor de pagos');
            }

        } catch (error) {
            console.error('Error al generar preferencia de pago:', error);
            setPaymentError(error.message || 'Error al generar preferencia de pago');
            toast.error(error.message || 'Error al generar preferencia de pago');
        } finally {
            setIsLoadingPayment(false);
        }
    }, [turno, afiliado, pagoStatus, esPagoBonificado, tarifaNumerica]);

    useEffect(() => {
        console.log('DATOS TURNO CONETENDOR TURNO:', turno)
    }, [turno])

    return ( <>

            {
                loading && <p>Cargando...</p>
            }

            {
                error && <p>Hubo un error en la carga de datos</p>
            }
            
            {
                turno && Object.keys(turno).length > 0 && 
                <Contenedor
                    titulo={<span><span className="fw-bold">Turno N° {turno.id}</span> del día {formatDate(turno.fechaCalendario.fecha)}</span>}
                    className="my-3"
                    actionButton={<ButtonVolver className="d-print-none">Volver</ButtonVolver>}
                >
                    {
                        turno.estadoTurnoID === enumEstadoTurno.ACTIVO.id &&
                        <AlertaTurno className="alert d-print-none mb-1" role="alert">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-3"/>
                                Recuerde MOSTRAR en la entrada del CENTRO RECREATIVO el turno con la marca de PAGO de acceso al AREA PROTEGIDA.
                            <ul className="mt-2">
                                <li>Los invitados solo pueden ingresar en presencia del TITULAR (con DNI)</li>
                                <li>Puede mostrarse la captura de pantalla del QR del turno</li>
                                
                            </ul>
                        </AlertaTurno>
                    }

<div className={pagoStatus === 'approved' ? 'watermarked' : ''}>
    <div className="d-flex flex-column align-items-center">
        {
            turno.titularPresente &&
            <>
            <TituloFiltro className="mb-0">Titular</TituloFiltro>
            <label className="">{afiliado.dni} - {afiliado.nombreCompleto}</label>
            </>
            
        }
        

        {
            turno.familiaresAsistentes.length > 0 &&
            <>
                <TituloFiltro className="mb-0 mt-3">Grupo familiar</TituloFiltro>
                {
                    turno.familiaresAsistentes.map((mochila) =>
                        <label
                            className=""
                            key={mochila.familiarDNI} 
                        >
                            {mochila.familiarDNI} - {mochila.familiar.apellido + ", " + mochila.familiar.nombre}
                        </label>
                    )
                }
            </>
        }
        {
            turno.invitados.length > 0 &&
            <>
                <TituloFiltro className="mb-0 mt-3">Grupo Invitados</TituloFiltro>
                <ListaInvitados
                    invitados={turno.invitados}
                />
            </>
        }
    </div>

    <div className="d-flex flex-column align-items-center mt-3">
        <TituloFiltro className="mb-1 mt-3">Estado de turno</TituloFiltro>
        <BadgeEstadoTurno estadoID={turno.estadoTurnoID} size="md" className=""/>
        <label className="py-3">{formatDate(turno.fechaCalendario.fecha) + " - " + turno.fechaCalendario.centroRecreativo.descripcion}</label>
        <div style={{ background: 'white', padding: '16px' }}>
        <QRCode 
        value={turno.id.toString()} 
        size={140} 
        bgColor="transparent"
        fgColor="#000"
    />
    </div>
    </div>
</div>

                    {/* Sección de Pago */}
                    {turno.estadoTurnoID === enumEstadoTurno.ACTIVO.id && (
                        <div className="mt-4 p-3 border rounded">
                            <TituloFiltro className="mb-3">
                                <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                                Pago del Turno
                            </TituloFiltro>

                            {/* Información del pago */}
                            {!isLoadingPagoData && !isLoadingPayment && (
                                 <InfoBox className="mb-3">
                                 <div className="row">
                                     <div className="col-md-6">
                                         <div className="mb-2">
                                             <TextMuted>Turno N°:</TextMuted>
                                             <span className="fw-bold ms-2">{turno.id}</span>
                                         </div>
                                         <div className="mb-2">
                                             <TextMuted>Fecha:</TextMuted>
                                             <span className="ms-2">{formatDate(turno.fechaCalendario.fecha)}</span>
                                         </div>
                                         <div className="mb-2">
                                             <TextMuted>Centro:</TextMuted>
                                             <span className="ms-2">{turno.fechaCalendario.centroRecreativo.descripcion}</span>
                                         </div>
                                         <div>
                                             <TextMuted>Monto:</TextMuted>
                                             <span className="ms-2">{montoDisplay}</span>
                                         </div>
                                     </div>
                                    
                                 </div>
                             </InfoBox>
                            )}

                            {/* Estado de carga de datos de pago */}
                            {isLoadingPagoData && (
                                <div className="text-center py-3">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Consultando estado de pago...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Consultando estado de pago...</p>
                                </div>
                            )}

                            {/* Estado de carga del pago */}
                            {isLoadingPayment && (
                                <div className="text-center py-3">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Generando preferencia de pago...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Generando preferencia de pago...</p>
                                </div>
                            )}

                            {/* Error en el pago */}
                            {paymentError && (
                                <div className="alert alert-danger" role="alert">
                                    <strong>Error:</strong> {paymentError}
                                    <button 
                                        className="btn btn-sm btn-outline-danger ms-2" 
                                        onClick={() => setPaymentError(null)}
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            )}

            {/* PAGO APROBADO */}
                            {pagoStatus === 'approved' && !isLoadingPagoData && (
                                <div className="alert alert-success" role="alert">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    <strong>Pago realizado:</strong> El pago del turno {turno.id} ha sido procesado exitosamente.
                                    <div className="mt-2 pt-2 border-top">
                                        <strong>Monto abonado:</strong> 
                                        <span className="ms-2 fs-5 fw-bold">
                                            {montoDisplay}
                                        </span>
                                    </div>
                                </div>
                            )}

            {/* PAGO PENDIENTE */}
            {pagoStatus === 'pending' && !isLoadingPagoData && (
                <div className="alert alert-warning" role="alert">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Pago pendiente:</strong> Tu pago está en proceso de validación. Te notificaremos cuando se acredite.
                    <div className="mt-2 pt-2 border-top">
                        <strong>Monto a abonar:</strong> 
                        <span className="ms-2 fs-5 fw-bold">
                            {montoDisplay}
                        </span>
                    </div>
                </div>
            )}

            {/* TURNO VENCIDO - Se muestra si el pago está expirado o si la fecha del turno es pasada, pero NO si está pagado */}
            {(pagoStatus === 'expired' || estadoFechaTurno === 'pasado') && pagoStatus !== 'approved' && !isLoadingPagoData && (
                <div className="alert alert-warning" role="alert">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    <strong>TURNO VENCIDO</strong>
                    <p className="mb-0 mt-2">El tiempo para realizar el pago ha expirado. El turno ya no está disponible para pago.</p>
                </div>
            )}

            {/* PAGO CREADO O RECHAZADO (mostrar Brick para pagar o reintentar) - Solo si es HOY */}
            {(pagoStatus === 'rejected' || pagoStatus === 'created') && !isLoadingPagoData && !isLoadingPayment && preferenceId && estadoFechaTurno === 'hoy' && (
                <>
                    {pagoStatus === 'rejected' && (
                        <div className="alert alert-danger mb-3" role="alert">
                            <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                            <strong>Pago rechazado:</strong> No se pudo procesar el pago. Por favor, revisa los datos e intenta nuevamente.
                            <div className="mt-2 pt-2 border-top">
                                <strong>Monto a abonar:</strong> 
                                <span className="ms-2 fs-5 fw-bold">
                                    {montoDisplay}
                                </span>
                            </div>
                        </div>
                    )}
                    {pagoStatus === 'created' && (
                        <div className="alert alert-info mb-3" role="alert">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            Completa el pago para confirmar tu reserva.
                            <div className="mt-2 pt-2 border-top">
                                <strong>Monto a abonar:</strong> 
                                <span className="ms-2 fs-5 fw-bold">
                                    {montoDisplay}
                                </span>
                            </div>
                        </div>
                    )}
                    <MercadoPagoBrick 
                        preferenceId={preferenceId}
                        idPago={idPago}
                        tarifa={turno.tarifa}
                        onPaymentResult={(status) => {
                            // Actualizar estado local según respuesta del Brick
                            if (status === 'approved') {
                                setPagoStatus('approved');
                                setPreferenceId(null);
                            } else if (status === 'rejected') {
                                // Mantener el estado como 'rejected' pero mantener preferenceId para reintentar
                                setPagoStatus('rejected');
                            } else if (status === 'pending') {
                                setPagoStatus('pending');
                            }
                        }}
                    />
                </>
            )}

            {/* MENSAJE PARA TURNO FUTURO */}
            {(pagoStatus === 'rejected' || pagoStatus === 'created' || pagoStatus === 'not_found') && !isLoadingPagoData && estadoFechaTurno === 'futuro' && (
                <div className="alert alert-info" role="alert">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    <strong>Información:</strong> Podrá pagar la reserva el mismo día del turno.
                </div>
            )}

                            {/* NO EXISTE PAGO (mostrar formulario para crear) - Solo si es HOY */}
                            {pagoStatus === 'not_found' && !isLoadingPagoData && !isLoadingPayment && !paymentError && estadoFechaTurno === 'hoy' && tarifaNumerica !== 0 && (
                                <div className="text-center">
                                    <p className="text-muted mb-3">
                                        Para completar su reserva, debe realizar el pago del turno.
                                    </p>
                                    <div className="mb-3">
                                        <div className="text-muted small">Monto a abonar</div>
                                        <div className={`fs-4 fw-bold ${esPagoBonificado ? 'text-primary' : 'text-success'}`}>
                                            {montoDisplay}
                                        </div>
                                    </div>
                                    <BotonSuccess
                                        onClick={handleGenerarPago}
                                        className="d-print-none"
                                    >
                                        <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                                        Pagar Reserva
                                    </BotonSuccess>
                                </div>
                            )}
                        </div>
                    )}

                    {
                        turno.estadoTurnoID === enumEstadoTurno.ACTIVO.id && pagoStatus !== 'approved' && pagoStatus !== 'expired' && estadoFechaTurno !== 'pasado' &&
                        <div className="d-flex justify-content-between mt-3">

                            <BotonSuccess
                                ancho="49%"
                                className="d-print-none py-1"
                                onClick={() => window.print()} 
                            >
                                <FontAwesomeIcon icon={faPrint} className="me-3 my-auto"/>
                                Imprimir
                            </BotonSuccess>

                            <ButtonConfirm
                                onClickConfirmar={anularTurno}
                                textoAlerta="¿Está seguro de anular el turno?"
                                color="danger"
                                textoBotonConfirmar="Confirmar anulación"
                                ancho="49%"
                                className="d-print-none py-1"
                            >
                                <FontAwesomeIcon icon={faTrashAlt} className="me-3 my-auto"/>
                                Anular
                            </ButtonConfirm>

                        </div>
                    }
                </Contenedor>
            }
        </>
    )
};

export default ContenedorTurno;
